import { tVector4 } from './ArrayHelper';
import Point, { IPointData } from './Point';

export interface ILimit {
    max: number;
    min: number;
}

export namespace MathHelper {
    /**
     * Linearly interpolates between a and b by t.
     * @param a The start value.
     * @param b The end value.
     * @param t The interpolation value between the two floats.
     */
    export function lerp(a: number, b: number, t: number): number {
        return a * (1 - t) + b * t;
    }

    /**
     * Linearly interpolates between points a and b by t.
     */
    export function lerpPoints(a: IPointData, b: IPointData, t: number): Point {
        return new Point(lerp(a.x, b.x, t), lerp(a.y, b.y, t));
    }

    /**
     * Clamps value between min and max and returns value.
     * @param value The value.
     * @param min The min value.
     * @param max The max value.
     */
    export function clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }

    /**
     * Loops the value t, so that it is never larger than length and never smaller than 0.
     * @param value The value.
     * @param length The length.
     */
    export function repeat(value: number, length: number): number {
        return ((value % length) + length) % length;
    }

    /**
     * @example
     * floor(40, 20); // 40
     * floor(41, 20); // 40
     * floor(42, 20); // 40
     * floor(55, 20); // 40
     * floor(60, 20); // 60
     */
    export function floor(value: number, base: number = 1, offset: number = 0) {
        if (equals(base, 0)) return value;
        return offset + base * Math.floor((value - offset) / base);
    }

    /**
     * @example
     * round(40, 20); // 40
     * round(41, 20); // 40
     * round(42, 20); // 40
     * round(55, 20); // 60
     * round(60, 20); // 60
     */
    export function round(value: number, base: number = 1, offset: number = 0) {
        if (equals(base, 0)) return value;
        return offset + base * Math.round((value - offset) / base);
    }

    /**
     * @example
     * ceil(40, 20); // 40
     * ceil(41, 20); // 60
     * ceil(42, 20); // 60
     * ceil(55, 20); // 60
     * ceil(60, 20); // 60
     */
    export function ceil(value: number, base: number = 1, offset: number = 0) {
        if (equals(base, 0)) return value;
        return offset + base * Math.ceil((value - offset) / base);
    }

    /** Compare floats with given precision */
    export function equals(a: number, b: number, epsilon: number = 1e-6) {
        return Math.abs(a - b) <= epsilon;
    }

    /**
     * Returns a value on a scale from `targetRangeStart` to `targetRangeEnd`
     * that corresponds to position of value `sourceValue` on a scale from
     * `sourceRangeStart` to `sourceRangeEnd`.
     * Can also be used for values outside the given ranges.
     * Ranges can also be inverted (begin from bigger value and end with a smaller one).
     *
     * @example
     *     remap(12, 10, 20, 300, 250); // returns 290
     *
     *     // 12 on a scale from 10 to 20
     *     // corresponds to 290 on a scale from 300 to 250
     *     //
     *     // sourceRangeStart                        sourceRangeEnd
     *     //       |                                       |
     *     //       |    sourceValue                        |
     *     //       |       |                               |
     *     //       v       v                               v
     *     // ...  10  11  12  13  14  15  16  17  18  19  20 ...
     *     // ---------------------------------------------------
     *     // ... 300 295 290 285 280 275 270 265 260 255 250 ...
     *     //       ^       ^                               ^
     *     //       |       |                               |
     *     //       |    targetValue                        |
     *     //       |                                       |
     *     // targetRangeStart                        targetRangeEnd
     */
    export function remap(
        sourceValue: number,
        sourceRangeStart: number,
        sourceRangeEnd: number,
        targetRangeStart: number,
        targetRangeEnd: number,
        clamp: boolean = false
    ): number {
        let result = lerp(
            targetRangeStart,
            targetRangeEnd,
            (sourceValue - sourceRangeStart) / (sourceRangeEnd - sourceRangeStart)
        );
        if (clamp) {
            const min = Math.min(targetRangeStart, targetRangeEnd);
            const max = Math.max(targetRangeStart, targetRangeEnd);
            if (result < min) result = min;
            if (result > max) result = max;
        }
        return result;
    }

    export function pingPong(x: number, l: number): number {
        let L = 2 * l;
        let T = x % L;

        if (T < l && T >= 0) {
            return T;
        } else {
            return L - T;
        }
    }

    /**
     * @param t progress, value from 0 (means p1) to 1 (means p4)
     * @param p1 start
     * @param p2 first control point
     * @param p3 second control point
     * @param p4 end
     */
    export function cubicBezier(
        t: number,
        p1: { x: number; y: number },
        p2: { x: number; y: number },
        p3: { x: number; y: number },
        p4: { x: number; y: number }
    ): { x: number; y: number } {
        const t1 = 1 - t;
        return {
            x: t1 * t1 * t1 * p1.x + 3 * t1 * t1 * t * p2.x + 3 * t1 * t * t * p3.x + t * t * t * p4.x,
            y: t1 * t1 * t1 * p1.y + 3 * t1 * t1 * t * p2.y + 3 * t1 * t * t * p3.y + t * t * t * p4.y,
        };
    }

    export function isWithinRange(value: number, min: number, max: number): boolean {
        return value >= min && value <= max;
    }

    /**
     * Verifies if the value is within the provided limit
     */
    export function isWithinRangeLimit(value: number, limits: ILimit): boolean {
        return isWithinRange(value, limits.min, limits.max);
    }

    /**
     * Normalizes a value between 0 and 1
     */
    export function normalize(value: number, rangeStart: number, rangeEnd: number): number {
        return MathHelper.remap(value, rangeStart, rangeEnd, 0, 1);
    }

    export function isOdd(value: number): boolean {
        return value % 2 !== 0;
    }

    export function addVector4(valueA: tVector4): (valueB: tVector4) => tVector4 {
        return valueB => {
            return valueA.map((val, index) => val + valueB[index]) as tVector4;
        };
    }

    /**
     * Gets the distance between 2 points
     */
    export function getDistance(a: IPointData, b: IPointData): number {
        return Math.hypot(a.x - b.x, a.y - b.y);
    }

    /**
     * @param a Point A on line
     * @param b Point B on line
     * @param distance Distance from point A to place new point at
     * @returns A new point at X distance away from point A
     */
    export function getPointOnLine(a: IPointData, b: IPointData, distance: number): IPointData {
        const distanceRatio = distance / getDistance(a, b);
        return {
            x: (1 - distanceRatio) * a.x + distanceRatio * b.x,
            y: (1 - distanceRatio) * a.y + distanceRatio * b.y,
        };
    }

    /**
     * @param a Point A on line
     * @param b Point B on line
     * @returns Slope of the line..
     */
    export function getSlopeOfLine(a: IPointData, b: IPointData): number {
        return (b.y - a.y) / (b.x - a.x);
    }

    /**
     * @param a Point A on line
     * @param b Point B on line
     * @returns Angle of line in radians
     */
    export function getAngleOfLine(a: IPointData, b: IPointData): number {
        const slope = getSlopeOfLine(a, b);
        return Math.atan(slope);
    }

    /**
     * Returns the smallest power of two greater than or equal to the supplied value
     */
    export function getNextPOT(value: number): number {
        return Math.pow(2, Math.ceil(Math.log2(value)));
    }

    /**
     * Abbreviates huge numbers using letters at the end (100k, 1M)
     * @param value The number to be abbreviated
     * @returns The abbreviated number in string format
     */
    export function abbreviateNumber(value: number): string {
        if (value >= 1000000) {
            return intlFormat(value / 1000000) + 'M';
        }

        if (value >= 1000) {
            return intlFormat(value / 1000) + 'K';
        }

        return value.toString();

        function intlFormat(value: number) {
            return new Intl.NumberFormat().format(Math.floor(value * 10) / 10);
        }
    }
}
